import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore } from '../store/gameStore';

interface PrestigeUpgradeCardProps {
  title: string;
  description: string;
  icon: string;
  level: number;
  cost: number;
  effect: string;
  canAfford: boolean;
  onPurchase: () => void;
  onBulkPurchase: (quantity: number) => void;
  upgradeType: string;
}

const PrestigeUpgradeCard: React.FC<PrestigeUpgradeCardProps> = ({
  title,
  description,
  icon,
  level,
  cost,
  effect,
  canAfford,
  onPurchase,
  onBulkPurchase,
  upgradeType,
}) => {
  const { prestige, getPrestigeUpgradeCost, formatNumber } = useGameStore();
  
  // Berechne Kosten für Bulk-Käufe
  const calculateBulkCost = (quantity: number) => {
    let totalCost = 0;
    for (let i = 0; i < quantity; i++) {
      totalCost += getPrestigeUpgradeCost(upgradeType as any, level + i);
    }
    return totalCost;
  };

  const bulkOptions = [10, 25, 100];
  
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className={`bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg p-2 sm:p-3 border transition-all ${
        canAfford ? 'border-purple-300 hover:border-purple-400' : 'border-gray-200 opacity-60'
      }`}
    >
      <div className="text-center">
        <div className="text-2xl sm:text-3xl mb-1">{icon}</div>
        <h3 className="font-bold text-sm sm:text-base mb-0.5">{title}</h3>
        <div className="text-xs text-purple-600 font-medium mb-0.5">Level {level}</div>
        <p className="text-xs text-gray-600 mb-2 leading-tight">{description}</p>
        
        <div className="bg-purple-100 rounded p-1.5 mb-2">
          <div className="text-xs text-purple-700 font-medium">{effect}</div>
        </div>
        
        <div className="flex justify-between items-center text-xs mb-2">
          <span className="text-gray-600">Kosten:</span>
          <span className="font-bold text-purple-600">⭐ {formatNumber(cost)}</span>
        </div>
        
        {/* Einzelkauf Button - größer für Touch */}
        <motion.button
          whileHover={canAfford ? { scale: 1.05 } : {}}
          whileTap={canAfford ? { scale: 0.95 } : {}}
          onClick={onPurchase}
          disabled={!canAfford}
          className={`w-full py-1.5 sm:py-2 px-2 sm:px-3 rounded font-medium text-xs sm:text-sm mb-1.5 transition-colors ${
            canAfford
              ? 'bg-purple-500 text-white hover:bg-purple-600'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          {canAfford ? 'Kaufen' : 'Nicht genug Punkte'}
        </motion.button>
        
        {/* Bulk-Kauf Buttons - größer für Touch */}
        <div className="grid grid-cols-3 gap-1">
          {bulkOptions.map(quantity => {
            const bulkCost = calculateBulkCost(quantity);
            const canAffordBulk = (prestige?.points || 0) >= bulkCost;
            
            return (
              <motion.button
                key={quantity}
                whileHover={canAffordBulk ? { scale: 1.05 } : {}}
                whileTap={canAffordBulk ? { scale: 0.95 } : {}}
                onClick={() => onBulkPurchase(quantity)}
                disabled={!canAffordBulk}
                className={`py-1 px-1.5 rounded text-xs font-medium transition-colors ${
                  canAffordBulk
                    ? 'bg-indigo-400 text-white hover:bg-indigo-500'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
                title={`${quantity}x für ⭐ ${formatNumber(bulkCost)}`}
              >
                {quantity}x
              </motion.button>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
};

export const PrestigeSection: React.FC = () => {
  const {
    prestige,
    playerLevel,
    achievements,
    canPrestige,
    calculatePrestigePoints,
    performPrestige,
    purchasePrestigeUpgrade,
    purchasePrestigeUpgradeBulk,
    getPrestigeUpgradeCost,
    formatNumber,
    canMegaPrestige,
    performMegaPrestige,
    getMegaPrestigeMultiplier,
  } = useGameStore();
  
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedTab, setSelectedTab] = useState<'overview' | 'upgrades'>('overview');
  const [overviewView, setOverviewView] = useState<'prestige' | 'benefits'>('prestige');
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const prestigeUpgradeConfigs = [
    {
      key: 'clickMultiplier' as const,
      title: 'Klick-Verstärker',
      description: 'Permanente Erhöhung der Klick-Kraft',
      icon: '👆',
      effect: `+${(prestige?.permanentBonuses?.clickMultiplier || 0) * 50}% Klick-Bonus`,
    },
    {
      key: 'incomeMultiplier' as const,
      title: 'Einkommens-Boost',
      description: 'Mehr passives Einkommen aus allen Quellen',
      icon: '💰',
      effect: `+${(prestige?.permanentBonuses?.incomeMultiplier || 0) * 25}% Passiv-Einkommen`,
    },
    {
      key: 'xpMultiplier' as const,
      title: 'Erfahrungs-Multiplikator',
      description: 'Schnelleres Leveln durch mehr XP',
      icon: '⭐',
      effect: `+${(prestige?.permanentBonuses?.xpMultiplier || 0) * 30}% XP-Gewinn`,
    },
    {
      key: 'startingCoins' as const,
      title: 'Startkapital',
      description: 'Beginne mit mehr Münzen nach Prestige',
      icon: '🏦',
      effect: `+${useGameStore.getState().formatNumber((prestige?.permanentBonuses?.startingCoins || 0) * 50)} Startmünzen`,
    },
    {
      key: 'gemChance' as const,
      title: 'Edelstein-Glück',
      description: 'Höhere Chance auf Edelsteine',
      icon: '💎',
      effect: `+${(prestige?.permanentBonuses?.gemChance || 0) * 2}% Edelstein-Chance`,
    },
    {
      key: 'autoClickerEfficiency' as const,
      title: 'Auto-Clicker Effizienz',
      description: 'Auto-Clicker arbeiten effektiver',
      icon: '🤖',
      effect: `+${(prestige?.permanentBonuses?.autoClickerEfficiency || 0) * 30}% Auto-Clicker Bonus`,
    },
    {
      key: 'upgradeDiscount' as const,
      title: 'Upgrade-Rabatt',
      description: 'Alle Upgrades kosten weniger',
      icon: '🏷️',
      effect: `${(prestige?.permanentBonuses?.upgradeDiscount || 0) * 5}% Rabatt auf Upgrades`,
    },
    {
      key: 'gardenEfficiency' as const,
      title: 'Garten-Effizienz',
      description: 'Gartenbereiche bringen mehr Ertrag',
      icon: '🌱',
      effect: `+${(prestige?.permanentBonuses?.gardenEfficiency || 0) * 20}% Garten-Bonus`,
    },
  ];

  const handlePrestige = () => {
    performPrestige();
    setShowConfirmation(false);
  };

  // Touch handlers für Swipe-Funktionalität
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && overviewView === 'prestige') {
      setOverviewView('benefits');
    } else if (isRightSwipe && overviewView === 'benefits') {
      setOverviewView('prestige');
    }
  };

  // Keyboard navigation für PC
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft' && overviewView === 'benefits') {
      setOverviewView('prestige');
    } else if (e.key === 'ArrowRight' && overviewView === 'prestige') {
      setOverviewView('benefits');
    }
  };

  // Mouse click navigation
  const handleViewChange = (view: 'prestige' | 'benefits') => {
    setOverviewView(view);
  };

  const prestigePointsToGain = (() => {
    try {
      if (!calculatePrestigePoints) return 0;
      const points = calculatePrestigePoints();
      return isNaN(points) || !isFinite(points) ? 0 : points;
    } catch (error) {
      console.error('Fehler beim Berechnen der Prestige-Punkte:', error);
      return 0;
    }
  })();
  const canPerformPrestige = canPrestige ? canPrestige() : false;

  const getRequirements = () => {
    const prestigeLevel = prestige?.level || 0;
    const totalPrestiges = prestige?.totalPrestiges || 0;
    
    // Dynamische Anforderungen basierend auf Prestige-Level und Gesamt-Prestiges
    const baseLevel = 100;
    const levelIncrement = 15 + Math.floor(prestigeLevel / 2); // Steigt mit Prestige-Level
    const requiredLevel = baseLevel + (prestigeLevel * levelIncrement);
    
    // Münzen-Anforderung steigt exponentiell
    const baseCoins = 100000; // 100K für erstes Prestige
    const coinMultiplier = 1.5 + (prestigeLevel * 0.3); // Steigt mit jedem Prestige
    const requiredCoins = Math.floor(baseCoins * Math.pow(coinMultiplier, prestigeLevel));
    
    // Gartenbereich-Anforderung steigt um 30 jedes Prestige
    const baseGardenLevel = 6;
    const gardenLevelIncrement = prestigeLevel * 30;
    const requiredGardenLevel = baseGardenLevel + gardenLevelIncrement;
    
    // Neue Anforderungen für höhere Prestige-Levels
    const requirements = [
      { 
        met: (playerLevel || 1) >= requiredLevel, 
        text: `Level ${requiredLevel} erreichen (${playerLevel || 1}/${requiredLevel})`,
        description: `Steigt um ${levelIncrement} pro Prestige-Level`
      },
      { 
        met: (achievements?.totalCoinsEarned || 0) >= requiredCoins, 
        text: `${useGameStore.getState().formatNumber(requiredCoins)} Münzen verdienen (${useGameStore.getState().formatNumber(achievements?.totalCoinsEarned || 0)}/${useGameStore.getState().formatNumber(requiredCoins)})`,
        description: `Steigt exponentiell mit Prestige-Level`
      },
      { 
        met: Object.values(useGameStore.getState().gardenAreas || {}).some(area => area.level >= requiredGardenLevel), 
        text: `Mindestens einen Gartenbereich auf Level ${requiredGardenLevel}`,
        description: `Steigt um 30 jedes Prestige`
      },
    ];
    
    // Zusätzliche Anforderungen für höhere Prestige-Levels
    if (prestigeLevel >= 5) {
      const requiredUpgrades = Math.floor(prestigeLevel / 2) + 3;
      const currentUpgrades = Object.values(useGameStore.getState().upgrades || {}).filter(level => level > 0).length;
      requirements.push({
        met: currentUpgrades >= requiredUpgrades,
        text: `${requiredUpgrades} verschiedene Upgrades kaufen (${currentUpgrades}/${requiredUpgrades})`,
        description: `Ab Prestige-Level 5`
      });
    }
    
    if (prestigeLevel >= 10) {
      const requiredCompanions = Math.floor(prestigeLevel / 3) + 2;
      const currentCompanions = useGameStore.getState().companions?.discovered?.length || 0;
      requirements.push({
        met: currentCompanions >= requiredCompanions,
        text: `${requiredCompanions} Begleiter entdecken (${currentCompanions}/${requiredCompanions})`,
        description: `Ab Prestige-Level 10`
      });
    }
    
    if (prestigeLevel >= 15) {
      const requiredMatchThreeWins = Math.floor(prestigeLevel / 2) + 5;
      const currentWins = achievements?.matchThreeWins || 0;
      requirements.push({
        met: currentWins >= requiredMatchThreeWins,
        text: `${requiredMatchThreeWins} Match-3 Spiele gewinnen (${currentWins}/${requiredMatchThreeWins})`,
        description: `Ab Prestige-Level 15`
      });
    }
    
    if (prestigeLevel >= 20) {
      const requiredDailyStreak = Math.floor(prestigeLevel / 4) + 7;
      const currentStreak = achievements?.dailyStreakRecord || 0;
      requirements.push({
        met: currentStreak >= requiredDailyStreak,
        text: `${requiredDailyStreak} Tage Daily Streak (${currentStreak}/${requiredDailyStreak})`,
        description: `Ab Prestige-Level 20`
      });
    }
    
    if (prestigeLevel >= 25) {
      const requiredTerritories = Math.floor(prestigeLevel / 5) + 2;
      const conqueredTerritories = Object.keys(useGameStore.getState().companions?.conquest?.territoryConquestCounts || {}).length;
      requirements.push({
        met: conqueredTerritories >= requiredTerritories,
        text: `${requiredTerritories} Territorien erobern (${conqueredTerritories}/${requiredTerritories})`,
        description: `Ab Prestige-Level 25`
      });
    }
    
    return requirements;
  };

  const megaPrestigeMultiplier = getMegaPrestigeMultiplier();

  return (
    <div className="prestige-section space-y-3 sm:space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-100 via-indigo-100 to-purple-100 rounded-xl p-3 sm:p-6 shadow-lg">
        <div className="text-center">
          <h2 className="text-xl sm:text-3xl font-bold mb-2 bg-gradient-to-r from-purple-700 to-indigo-600 bg-clip-text text-transparent">
            ⭐ Prestige-System
          </h2>
          <p className="text-xs sm:text-base text-gray-600 mb-3 sm:mb-4">
            Setze deinen Fortschritt zurück und erhalte permanente Boni!
          </p>
          
          {/* Prestige Stats - Mobile-optimiert */}
          <div className="grid grid-cols-2 gap-1.5 sm:gap-2 mb-2 sm:mb-3">
            <div className="bg-white rounded-lg p-2 sm:p-3 shadow-sm">
              <div className="text-lg sm:text-2xl font-bold text-purple-600">{useGameStore.getState().formatNumber(prestige?.level || 0)}</div>
              <div className="text-xs text-gray-600">Prestige Level</div>
            </div>
            <div className="bg-white rounded-lg p-2 sm:p-3 shadow-sm">
              <div className="text-lg sm:text-2xl font-bold text-indigo-600">{useGameStore.getState().formatNumber(prestige?.points || 0)}</div>
              <div className="text-xs text-gray-600">Verfügbare Punkte</div>
            </div>
            <div className="bg-white rounded-lg p-2 sm:p-3 shadow-sm">
              <div className="text-lg sm:text-2xl font-bold text-blue-600">{useGameStore.getState().formatNumber(prestige?.totalPoints || 0)}</div>
              <div className="text-xs text-gray-600">Gesamt-Punkte</div>
            </div>
            <div className="bg-white rounded-lg p-2 sm:p-3 shadow-sm">
              <div className="text-lg sm:text-2xl font-bold text-green-600">{useGameStore.getState().formatNumber(prestige?.totalPrestiges || 0)}</div>
              <div className="text-xs text-gray-600">Prestige-Anzahl</div>
            </div>
            <div className="bg-white rounded-lg p-2 sm:p-3 shadow-sm col-span-2">
              <div className="text-lg sm:text-2xl font-bold text-orange-600">{useGameStore.getState().formatNumber(prestigePointsToGain)}</div>
              <div className="text-xs text-gray-600">Zu erwartende Punkte</div>
            </div>
          </div>

          {/* Tabs - Mobile-optimiert */}
          <div className="flex justify-center gap-1 sm:gap-2 mb-3 sm:mb-4">
            {[
              { key: 'overview', label: 'Übersicht', icon: '📊' },
              { key: 'upgrades', label: 'Upgrades', icon: '⬆️' },
            ].map(tab => (
              <motion.button
                key={tab.key}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedTab(tab.key as any)}
                className={`px-2 sm:px-4 py-2 sm:py-3 rounded-lg font-medium text-xs sm:text-sm transition-all flex items-center gap-1 sm:gap-2 flex-1 sm:flex-initial sm:min-w-[120px] ${
                  selectedTab === tab.key
                    ? 'bg-purple-500 text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <span className="text-sm sm:text-base">{tab.icon}</span>
                <span className="text-xs sm:text-sm">{tab.label}</span>
              </motion.button>
            ))}
          </div>
        </div>
      </div>

      {/* Content based on selected tab */}
      <AnimatePresence mode="wait" key="tab-content">
        {selectedTab === 'overview' && (
          <motion.div
            key="overview"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-3 sm:space-y-6"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onKeyDown={handleKeyDown}
            tabIndex={0}
          >
            {/* Navigation Controls */}
            <div className="flex items-center justify-center mb-2 space-x-4">
              {/* Left Arrow */}
              <button
                onClick={() => handleViewChange('prestige')}
                disabled={overviewView === 'prestige'}
                className={`p-1 rounded-full transition-all duration-200 ${
                  overviewView === 'prestige' 
                    ? 'text-gray-300 cursor-not-allowed' 
                    : 'text-purple-500 hover:text-purple-600 hover:bg-purple-50'
                }`}
                aria-label="Zurück zu Prestige durchführen"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              {/* Navigation Dots */}
              <div className="flex space-x-2">
                <button
                  onClick={() => handleViewChange('prestige')}
                  className={`w-3 h-3 rounded-full transition-all duration-200 hover:scale-110 ${
                    overviewView === 'prestige' ? 'bg-purple-500' : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label="Prestige durchführen anzeigen"
                />
                <button
                  onClick={() => handleViewChange('benefits')}
                  className={`w-3 h-3 rounded-full transition-all duration-200 hover:scale-110 ${
                    overviewView === 'benefits' ? 'bg-purple-500' : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label="Was passiert beim Prestige anzeigen"
                />
              </div>

              {/* Right Arrow */}
              <button
                onClick={() => handleViewChange('benefits')}
                disabled={overviewView === 'benefits'}
                className={`p-1 rounded-full transition-all duration-200 ${
                  overviewView === 'benefits' 
                    ? 'text-gray-300 cursor-not-allowed' 
                    : 'text-purple-500 hover:text-purple-600 hover:bg-purple-50'
                }`}
                aria-label="Weiter zu Was passiert beim Prestige"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            <AnimatePresence mode="wait">
              {overviewView === 'prestige' && (
                <motion.div
                  key="prestige"
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 50 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-xl p-3 sm:p-6 shadow-md"
                >
                  <h3 className="text-base sm:text-xl font-bold mb-3 sm:mb-4 text-center">🚀 Prestige durchführen</h3>
                  
                  {canPerformPrestige ? (
                    <div className="text-center">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4 mb-3 sm:mb-4">
                        <div className="text-green-800 font-bold mb-2 text-sm sm:text-base">Du kannst Prestige durchführen!</div>
                        <div className="text-green-700 text-xs sm:text-base">
                          Du erhältst <strong>{useGameStore.getState().formatNumber(prestigePointsToGain)} Prestige-Punkte</strong>
                        </div>
                      </div>
                      
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setShowConfirmation(true)}
                        className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white px-4 sm:px-8 py-3 sm:py-4 rounded-lg font-bold text-sm sm:text-lg shadow-lg hover:from-purple-600 hover:to-indigo-600 transition-all w-full sm:w-auto"
                      >
                        ⭐ Prestige durchführen
                      </motion.button>
                    </div>
                  ) : (
                    <div className="text-center">
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4 mb-3 sm:mb-4">
                        <div className="text-gray-800 font-bold mb-2 sm:mb-3 text-sm sm:text-base">Anforderungen für Prestige:</div>
                        <div className="space-y-1 sm:space-y-2 text-center">
                          {getRequirements().map((req, index) => (
                            <div key={index} className={`${req.met ? 'text-green-600' : 'text-red-600'}`}>
                              <div className="text-xs sm:text-sm">{req.text}</div>
                              {req.description && (
                                <div className="text-xs text-gray-500 mt-0.5">{req.description}</div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              )}

              {overviewView === 'benefits' && (
                <motion.div
                  key="benefits"
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-xl p-3 sm:p-6 shadow-md"
                >
                  <h3 className="text-base sm:text-xl font-bold mb-3 sm:mb-4 text-center">🎁 Was passiert beim Prestige?</h3>
                  
                  <div className="space-y-3 sm:space-y-4 text-center">
                    <div className="space-y-2 sm:space-y-3">
                      <h4 className="font-semibold text-green-600 text-sm sm:text-base">✅ Was du behältst:</h4>
                      <ul className="space-y-1 text-xs sm:text-sm">
                        <li>• Alle permanenten Prestige-Boni</li>
                        <li>• 10% deiner Edelsteine</li>
                        <li>• Deine Daily Streak Rekord</li>
                        <li>• Prestige-Punkte zum Ausgeben</li>
                      </ul>
                    </div>
                    
                    <div className="space-y-2 sm:space-y-3">
                      <h4 className="font-semibold text-red-600 text-sm sm:text-base">❌ Was zurückgesetzt wird:</h4>
                      <ul className="space-y-1 text-xs sm:text-sm">
                        <li>• Level zurück auf 1</li>
                        <li>• Alle Upgrades</li>
                        <li>• Gartenbereiche</li>
                        <li>• Match-3 Fortschritt</li>
                        <li>• 90% der Edelsteine</li>
                      </ul>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}

        {selectedTab === 'upgrades' && (
          <motion.div
            key="upgrades"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <div className="bg-white rounded-xl p-3 sm:p-6 shadow-md">
              <h3 className="text-base sm:text-xl font-bold mb-3 sm:mb-4 text-center">⬆️ Permanente Upgrades</h3>
              <p className="text-center text-gray-600 mb-4 sm:mb-6 text-xs sm:text-base">
                Verwende deine Prestige-Punkte für permanente Verbesserungen!
              </p>
              
              {/* Mobile-optimiertes Grid: 1 Spalte auf Smartphones, mehr auf größeren Bildschirmen */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-3">
                {prestigeUpgradeConfigs.map((config) => {
                  const currentLevel = prestige?.permanentBonuses?.[config.key] || 0;
                  const cost = getPrestigeUpgradeCost ? getPrestigeUpgradeCost(config.key, currentLevel) : 0;
                  
                  return (
                    <PrestigeUpgradeCard
                      key={config.key}
                      title={config.title}
                      description={config.description}
                      icon={config.icon}
                      level={currentLevel}
                      cost={cost}
                      effect={config.effect}
                      canAfford={(prestige?.points || 0) >= cost}
                      onPurchase={() => purchasePrestigeUpgrade && purchasePrestigeUpgrade(config.key)}
                      onBulkPurchase={(quantity) => purchasePrestigeUpgradeBulk && purchasePrestigeUpgradeBulk(config.key, quantity)}
                      upgradeType={config.key}
                    />
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirmation Modal - Mobile-optimiert */}
      <AnimatePresence key="confirmation-modal">
        {showConfirmation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="bg-white rounded-xl p-4 sm:p-6 max-w-md w-full shadow-xl max-h-[90vh] overflow-y-auto"
            >
              <div className="text-center">
                <div className="text-3xl sm:text-4xl mb-3 sm:mb-4">⚠️</div>
                <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4">Prestige bestätigen</h3>
                <p className="text-gray-600 mb-4 sm:mb-6 text-xs sm:text-base leading-relaxed">
                  Bist du sicher, dass du Prestige durchführen möchtest? 
                  Dies wird deinen gesamten Fortschritt zurücksetzen, aber du erhältst {useGameStore.getState().formatNumber(prestigePointsToGain)} Prestige-Punkte!
                </p>
                
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowConfirmation(false)}
                    className="flex-1 py-2 sm:py-3 px-3 sm:px-4 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors text-sm sm:text-base"
                  >
                    Abbrechen
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handlePrestige}
                    className="flex-1 py-2 sm:py-3 px-3 sm:px-4 bg-purple-500 text-white rounded-lg font-medium hover:bg-purple-600 transition-colors text-sm sm:text-base"
                  >
                    Bestätigen
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Mega-Prestige Bereich - Mobile-optimiert */}
      {(prestige.level >= 10 || prestige.megaPrestige.level > 0) && (
        <div className="bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 text-white rounded-lg p-3 sm:p-6 shadow-lg border-4 border-yellow-300">
          <div className="text-center">
            <h2 className="text-xl sm:text-3xl font-bold mb-3 sm:mb-4">🌟 MEGA-PRESTIGE 🌟</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 mb-3 sm:mb-4">
              <div className="bg-white/20 rounded-lg p-3 sm:p-4">
                <div className="text-lg sm:text-2xl font-bold">{prestige.megaPrestige.level}</div>
                <div className="text-xs sm:text-sm opacity-90">Mega-Prestige Level</div>
              </div>
              <div className="bg-white/20 rounded-lg p-3 sm:p-4">
                <div className="text-lg sm:text-2xl font-bold">{formatNumber(prestige.megaPrestige.totalPointsSpent)}</div>
                <div className="text-xs sm:text-sm opacity-90">Investierte Punkte</div>
              </div>
              <div className="bg-white/20 rounded-lg p-3 sm:p-4">
                <div className="text-lg sm:text-2xl font-bold">{formatNumber((megaPrestigeMultiplier - 1) * 100)}%</div>
                <div className="text-xs sm:text-sm opacity-90">Globaler Bonus</div>
              </div>
            </div>
            
            <div className="bg-white/10 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
              <h3 className="text-base sm:text-xl font-bold mb-2">🔥 Mega-Prestige Effekt</h3>
              <p className="text-xs sm:text-sm opacity-90 leading-relaxed">
                Jede Million investierte Prestige-Punkte gibt <strong>+1% Bonus auf ALLES</strong>!
              </p>
              <p className="text-xs opacity-75 mt-2">
                Aktueller Multiplikator: <strong>{formatNumber(megaPrestigeMultiplier)}x</strong>
              </p>
            </div>

            {canMegaPrestige() && (
              <div className="bg-red-600/30 border-2 border-red-400 rounded-lg p-3 sm:p-4 mb-3 sm:mb-4">
                <h3 className="text-base sm:text-xl font-bold mb-2">⚠️ WARNUNG ⚠️</h3>
                <p className="text-xs sm:text-sm mb-3 sm:mb-4 leading-relaxed">
                  Mega-Prestige setzt <strong>ALLES</strong> zurück (außer Begleitern)!<br/>
                  Alle {formatNumber(prestige.totalPoints)} Prestige-Punkte werden investiert.
                </p>
                <button
                  onClick={performMegaPrestige}
                  className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-bold py-2 sm:py-3 px-4 sm:px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg text-xs sm:text-base"
                >
                  🌟 MEGA-PRESTIGE DURCHFÜHREN 🌟
                </button>
              </div>
            )}

            {!canMegaPrestige() && (
              <div className="bg-gray-600/30 border-2 border-gray-400 rounded-lg p-3 sm:p-4">
                <p className="text-xs sm:text-sm opacity-75">
                  Mega-Prestige erfordert mindestens Prestige Level 10
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}; 